import { useScrollReveal } from '@/hooks/useScrollReveal';

const fadeIn = (visible: boolean, delay: string) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(24px)',
  transition: 'all 700ms ease-out',
  transitionDelay: delay,
});

export default function FounderMessagePage() {
  const [heroRef, heroVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const [imageRef, imageVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const [messageRef, messageVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const [closingRef, closingVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <div className="bg-background-50/95">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-8 lg:px-12">
        <div ref={heroRef} className="max-w-7xl mx-auto text-center">
          <span
            className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4"
            style={fadeIn(heroVisible, '0ms')}
          >
            / A Message From Leadership
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-4"
            style={fadeIn(heroVisible, '100ms')}
          >
            Founder & Chairman
          </h1>
          <p
            className="text-foreground-300 text-base md:text-lg max-w-2xl mx-auto font-body"
            style={fadeIn(heroVisible, '200ms')}
          >
            A personal message from the visionary behind T.M.T — Trusted Money Transfer.
          </p>
        </div>
      </section>

      {/* Founder Image & Message */}
      <section className="py-8 md:py-12 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
            {/* Image */}
            <div ref={imageRef} className="w-full lg:w-5/12 shrink-0">
              <div
                className="relative overflow-hidden rounded-2xl border border-primary-700/20"
                style={fadeIn(imageVisible, '0ms')}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/30 via-transparent to-transparent z-10" />
                <img
                  src="https://storage.helloreaddy.io/project_files/e63cb4d6-832c-48f6-a8a8-3d5a81b53170/6e9a206c-c76a-47fb-8411-9ba6d87c4e45_compressed_ChatGPT-Image-Aug-14-2026-04_38_41-PM.webp"
                  alt="Nael Jammoul — Founder & Chairman of T.M.T"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="mt-5 text-center lg:text-left">
                <p className="font-heading text-xl text-foreground-100 font-semibold">
                  Nael Jammoul
                </p>
                <p className="font-label text-xs tracking-widest uppercase text-primary-400 mt-1">
                  Founder & Chairman
                </p>
              </div>
            </div>

            {/* Message */}
            <div ref={messageRef} className="w-full lg:w-7/12">
              <div className="space-y-6 font-body text-foreground-300 text-base md:text-lg leading-relaxed">
                <p style={fadeIn(messageVisible, '0ms')}>
                  <strong className="text-foreground-100 font-semibold">
                    T.M.T – Trusted Money Transfer
                  </strong>
                  , our vision is simple: to build a trusted financial bridge that connects people,
                  businesses, and opportunities across borders.
                </p>

                <p style={fadeIn(messageVisible, '100ms')}>
                  We believe that money transfer is more than a transaction. It is about{' '}
                  <strong className="text-foreground-100 font-semibold">
                    trust, security, speed
                  </strong>
                  , and the people behind every transfer. Our commitment is to provide reliable and
                  professional solutions while continuously embracing technology and innovation.
                </p>

                <p style={fadeIn(messageVisible, '200ms')}>
                  From{' '}
                  <strong className="text-foreground-100 font-semibold">
                    Beirut to Geneva, Cairo, and beyond
                  </strong>
                  , we are building T.M.T with a clear purpose: to make moving money easier, safer,
                  and more accessible for our customers and partners.
                </p>

                <p style={fadeIn(messageVisible, '300ms')}>
                  Our journey is only beginning. With the trust of our customers, the dedication of
                  our team, and strong partnerships around the world, we will continue to grow and
                  create lasting value.
                </p>

                <p style={fadeIn(messageVisible, '400ms')}>
                  Thank you for being part of the T.M.T journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signature / Closing */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div ref={closingRef} className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-px bg-primary-400/40 mx-auto mb-8" />
          <p
            className="font-heading text-2xl md:text-3xl text-foreground-50 italic leading-relaxed mb-8"
            style={fadeIn(closingVisible, '0ms')}
          >
            &ldquo;Building trust, one transfer at a time.&rdquo;
          </p>
          <div style={fadeIn(closingVisible, '150ms')}>
            <p className="font-heading text-lg text-foreground-100 font-semibold">
              Nael Jammoul
            </p>
            <p className="font-label text-xs tracking-widest uppercase text-primary-400 mt-1">
              Founder & Chairman
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}